import { CreateImageRequest, CreatorProfileCustomUrl, GetCreatorProfileResponse, ImageUpload, UpdateCreatorProfileRequest } from '../types';
import { LitElement, css, html } from 'lit';
import { arrayBufferToBase64, isDefined } from '../helpers/utils';
import { customElement, property, query, state } from 'lit/decorators';
import { getCreatorProfile, updateCreatorProfile } from '../helpers/api/creator-profile-api';

import { ProxiTextArea } from './theme/proxi-textarea';
import { ProxiTextfield } from './theme/proxi-textfield';
import { blobToDataUrl } from '../helpers/files';
import { getMainPage } from '../helpers/page-name';
import piexif from 'piexifjs';
import { uploadImage } from '../helpers/api/image-api';

@customElement('creator-profile-form')
export class CreatorProfileForm extends LitElement {
    private readonly amplitude_callsite: string = 'Add Creator Profile Modal';



    @query('#formName')
    formName?: ProxiTextfield;

    @query('#formUsername')
    formUsername?: ProxiTextfield;

    @query('#formTagline')
    formTagline?: ProxiTextfield;

    @query('#formProfile')
    formProfile?: ProxiTextArea;

    @query('#facebookLink')
    facebookLink?: ProxiTextfield;

    @query('#linkedinLink')
    linkedinLink?: ProxiTextfield;

    @query('#twitterLink')
    twitterLink?: ProxiTextfield;

    @query('#instagramLink')
    instagramLink?: ProxiTextfield;

    @query('#tikTokLink')
    tikTokLink?: ProxiTextfield;

    @query('#pinterestLink')
    pinterestLink?: ProxiTextfield;

    @query('#formCustomLinks')
    customLinks?: HTMLSpanElement;

    @query('#fileInput')
    fileInput?: HTMLInputElement;

    @property({ type: Object })
    creatorProfile?: GetCreatorProfileResponse;

    @state()
    customUrls: CreatorProfileCustomUrl[] = [];

    @state()
    imageEditing?: 'profile' | 'cover' | undefined;

    @state()
    uploadedImage?: ImageUpload;

    updated(changedProperties: Map<string | number | symbol, unknown>) {
        if (changedProperties.has('creatorProfile') && isDefined(this.creatorProfile)) {

            this.customUrls = this.creatorProfile?.custom_urls.length > 0 ? this.creatorProfile?.custom_urls : [{cta: '', url: ''}];
        }
    }

    private getCustomLinks() {
        const links = this.customLinks;

        if (isDefined(links)) {
            const formattedUrls: CreatorProfileCustomUrl[] = [];
            Array.from(links.children).forEach((child, i) => {
                const customUrlElement: ProxiTextfield = this.shadowRoot?.getElementById(`formCustomLink-${i}`) as ProxiTextfield;
                const customUrlTextElement: ProxiTextfield = this.shadowRoot?.getElementById(`formCustomLinkText-${i}`) as ProxiTextfield;

                const customUrl = this.normalizeUrl(customUrlElement.value);
                const customUrlText = customUrlTextElement.value;
                if (customUrl !== undefined && customUrlText !== '') {
                    formattedUrls.push({ cta: customUrlText, url: customUrl });
                }
            });

            return formattedUrls;
        }

        return [];
    }

    // the urls need to be prefixed with https, add that in if they arent already
    private normalizeUrl(url: string | undefined) {
        if (url === undefined || url === '' || url === 'https://') return undefined;

        return url.startsWith('https://') || url.startsWith('http://') ? url : `https://${url}`;
    }

    private async saveProfileInfo() {
        if (isDefined(this.creatorProfile)) {
            let image_url = this.creatorProfile.image;
            if (isDefined(this.uploadedImage)) {
                const imageRequest: CreateImageRequest = {
                    name: this.uploadedImage.file.name,
                    data: arrayBufferToBase64(await this.uploadedImage.file.arrayBuffer()),
                };
                image_url = await uploadImage(this.amplitude_callsite, imageRequest, this.uploadedImage.orientation);
            }

            const creatorProfileRequest: UpdateCreatorProfileRequest = {
                username: this.formUsername?.value ?? '',
                name: this.formName?.value,
                tagline: this.formTagline?.value,
                description: this.formProfile?.value,
                image: image_url,
                cover_image: this.creatorProfile.cover_image ?? '',
                facebook_url: this.normalizeUrl(this.facebookLink?.value),
                linkedin_url: this.normalizeUrl(this.linkedinLink?.value),
                twitter_url: this.normalizeUrl(this.twitterLink?.value),
                pinterest_url: this.normalizeUrl(this.pinterestLink?.value),
                instagram_url: this.normalizeUrl(this.instagramLink?.value),
                tiktok_url: this.normalizeUrl(this.tikTokLink?.value),
                custom_urls: this.getCustomLinks(),
            };

            await updateCreatorProfile(this.amplitude_callsite, this.creatorProfile?._id.$oid, creatorProfileRequest);

            const updatedProfile = await getCreatorProfile(this.amplitude_callsite, this.creatorProfile._id.$oid);
            this.dispatchEvent(new CustomEvent('on-update-profile', {
                detail: {
                    updatedProfile
                }
            }));

            getMainPage().errorToastElement?.showToast('Profile updated successfully');
        }
    }

    private addCustomUrlInput() {
        this.customUrls = [{ cta: '', url: ''}, ...this.customUrls];
    }

    private preventDefault(event: any) {
        event.preventDefault();
    }

    private async processImage(file: File): Promise<ImageUpload | undefined> {
            try {
                const base64file = await blobToDataUrl(file);
                const exifData = piexif.load(base64file);
                return { file, orientation: exifData['0th'][274] };
            } catch {
                return { file, orientation: undefined} ;
            }

    }

    async onChangeProfilePhoto(e: CustomEvent) {
        const value = e.detail?.imageUrl ?? '';
        if (this.creatorProfile) {
            this.creatorProfile.image = value;
            await this.fetchAndUpdateProfile({
                image: value,
            });
        }
        this.requestUpdate();
    }


    async onChangeCoverPhoto(e: CustomEvent) {
        const value = e.detail?.imageUrl ?? '';
        if (this.creatorProfile) {
            this.creatorProfile.cover_image = value;
            await this.fetchAndUpdateProfile({
                cover_image: value,
            });
        }
        this.requestUpdate();
    }

    async fetchAndUpdateProfile(request: Partial<UpdateCreatorProfileRequest>) {
        if (!this.creatorProfile) {
            throw new Error('Creator profile is not defined');
        }

        const currentProfile = await this.createRequestFromProfile(this.creatorProfile);
        const updatedProfile = {
            ...currentProfile,
            ...request,
        };

        await updateCreatorProfile(this.amplitude_callsite, this.creatorProfile._id.$oid, updatedProfile);
        this.creatorProfile = await getCreatorProfile(this.amplitude_callsite, this.creatorProfile._id.$oid, false);
    }

    async createRequestFromProfile(profile: GetCreatorProfileResponse): Promise<UpdateCreatorProfileRequest> {
        return {
            username: profile.username ?? '',
            name: profile.name ?? '',
            tagline: profile.tagline ?? '',
            description: profile.description ?? '',
            image: profile.image ?? '',
            cover_image: profile.cover_image ?? '',
            facebook_url: profile.facebook_url ?? '',
            linkedin_url: profile.linkedin_url ?? '',
            twitter_url: profile.twitter_url ?? '',
            pinterest_url: profile.pinterest_url ?? '',
            instagram_url: profile.instagram_url ?? '',
            tiktok_url: profile.tiktok_url ?? '',
            custom_urls: profile.custom_urls ?? [],
        };
    }

    render() {
        return html`
            <form class="creator-form__wrapper">
                <span class="creator-form__heading">
                    <span>
                        <h2 class="creator-form__header">Profile Info</h2>
                        <div class="creator-form__subheader">This information will appear on your public Proxi creator page</div>
                    </span>

                    <mwc-button raised @click="${this.saveProfileInfo}" class="creator-form__action-button">
                        Save
                    </mwc-button>
                </span>
                <span class="creator-form__input-group" style="display: flex; align-items: flex-start; flex-direction: column; gap: 10px;">
                    <b>Profile Photo</b>
                    <div class="image-upload" style="display: flex; flex-direction: row; gap: 10px;">
                        ${this.creatorProfile?.image && this.imageEditing !== 'profile' ? html`
                            <div class="logo-container">
                                <img src="${this.creatorProfile?.image ?? ''}" alt="Current Profile Picture" style="border-radius: 5px; padding: 5px; max-width: 100%; max-height: 100px;">
                                <mwc-icon @click="${() => {this.imageEditing = 'profile';}}" class="edit-icon">edit</mwc-icon>
                            </div>
                        ` : html`
                            ${this.creatorProfile?.image ? html`
                                <mwc-icon @click="${() => {this.imageEditing = undefined;}}" class="edit-icon">close</mwc-icon>
                            ` : ''}
                            <image-upload-component
                                id="coverImageUpload"
                                style="flex: 2;"
                                .autoUpload="${true}"
                                @retreived-autoupload-url="${this.onChangeProfilePhoto}"
                                @changed-image-url="${this.onChangeProfilePhoto}"
                            ></image-upload-component>
                        `}
                    </div>
                     <b>Cover Photo</b>
                    <div class="image-upload" style="display: flex; flex-direction: row; gap: 10px;">
                        ${this.creatorProfile?.cover_image && this.imageEditing !== 'cover' ? html`
                            <div class="logo-container">
                                <img src="${this.creatorProfile?.cover_image ?? ''}" alt="Current Cover Picture" style="border-radius: 5px; padding: 5px; max-width: 100%; max-height: 100px;">
                                <mwc-icon @click="${() => {this.imageEditing = 'cover';}}" class="edit-icon">edit</mwc-icon>
                            </div>
                        ` : html`
                            ${this.creatorProfile?.cover_image ? html`
                                <mwc-icon @click="${() => {this.imageEditing = undefined;}}" class="edit-icon">close</mwc-icon>
                            ` : ''}
                            <image-upload-component
                                id="coverImageUpload"
                                style="flex: 2;"
                                .autoUpload="${true}"
                                @retreived-autoupload-url="${this.onChangeCoverPhoto}"
                                @changed-image-url="${this.onChangeCoverPhoto}"
                            ></image-upload-component>
                        `}
                    </div>

                </span>
                <span class="creator-form__input-group">
                    <proxi-textfield class="creator-form__input" .value=${this.creatorProfile?.name ?? ''} outlined label="Name" id="formName"></proxi-textfield>
                    <proxi-textfield class="creator-form__input" .value=${this.creatorProfile?.username ?? ''} outlined label="Username" id="formUsername"></proxi-textfield>
                </span>
                <proxi-textfield class="creator-form__input" outlined label="Tagline" id="formTagline" .value=${this.creatorProfile?.tagline ?? ''} placeholder="One Liner Describing Yourself">
                </proxi-textfield>
                <mwc-textarea style="margin-top: 10px" class="creator-form__input" outlined label="Profile" id="formProfile" .value=${this.creatorProfile?.description ?? ''} placeholder="Short description of who you are and what kind of content you share">
                </mwc-textarea>
                <span class="creator-form__input-group" style="display: flex; align-items: flex-start; flex-direction: column; gap: 10px;">
                    <span class="creator-form__link-wrapper--first">
                        <span style="display: flex;">
                            <h4>Custom Link Outs</h4>
                            <mwc-icon-button
                                class="backpack-sidebar__icon"
                                @click=${this.addCustomUrlInput}
                                icon="add">
                            </mwc-icon-button>
                        </span>
                        <span id="formCustomLinks">
                            ${this.customUrls.map((customUrl, i) => {
                                return html`
                                    <span style="display: flex;">
                                        <proxi-textfield class="creator-form__input" value=${customUrl?.cta ?? ''} outlined label="Custom Link Text" id="formCustomLinkText-${i}"></proxi-textfield>
                                        <proxi-textfield class="creator-form__input" value=${customUrl?.url ?? ''} outlined label="Custom Link URL" id="formCustomLink-${i}"></proxi-textfield>
                                    </span>
                                `;
                            })}
                        </span>
                    </span>
                    <span class="creator-form__link-wrapper--second">
                        <h4>Socials</h4>
                        <span class="creator-form__social-links">
                            <img class="creator-form__social-icon" src="/images/socials/facebook.png" alt="Facebook icon" />
                            <proxi-textfield class="creator-form__input" outlined placeholder="Facebook Link" id="facebookLink" value=${this.creatorProfile?.facebook_url ?? ''}></proxi-textfield>
                        </span>
                        <span class="creator-form__social-links">
                            <img class="creator-form__social-icon" src="/images/socials/linkedin.png" alt="Linkedin icon" />
                            <proxi-textfield class="creator-form__input" outlined placeholder="LinkedIn Link" id="linkedinLink" value=${this.creatorProfile?.linkedin_url ?? ''}></proxi-textfield>
                        </span>
                        <span class="creator-form__social-links">
                            <img class="creator-form__social-icon" src="/images/socials/twitter.png" alt="Twitter icon" />
                            <proxi-textfield class="creator-form__input" outlined placeholder="Twitter/X Link" id="twitterLink" value=${this.creatorProfile?.twitter_url ?? ''}></proxi-textfield>
                        </span>
                        <span class="creator-form__social-links">
                            <img class="creator-form__social-icon" src="/images/socials/instagram.png" alt="Instagram icon" />
                            <proxi-textfield class="creator-form__input" outlined placeholder="Instagram Link" id="instagramLink" value=${this.creatorProfile?.instagram_url ?? ''}></proxi-textfield>
                        </span>
                        <span class="creator-form__social-links">
                            <img class="creator-form__social-icon" src="/images/socials/tiktok.png" alt="TikTok icon" />
                            <proxi-textfield class="creator-form__input" outlined placeholder="TikTok Link" id="tikTokLink" value=${this.creatorProfile?.tiktok_url ?? ''}></proxi-textfield>
                        </span>
                        <span class="creator-form__social-links">
                            <img class="creator-form__social-icon" src="/images/socials/pinterest.png" alt="Pinterest icon" />
                            <proxi-textfield class="creator-form__input" outlined placeholder="Pinterest Link" id="pinterestLink" value=${this.creatorProfile?.pinterest_url ?? ''}></proxi-textfield>
                        </span>
                    </span>
                </span>
            </form>

            <error-toast id="errorToast"></error-toast>
        `;
    }

    static get styles() {
        return css`
            .creator-form__wrapper {
                overflow: auto;
                margin-bottom: 4rem;
            }

            .creator-form__heading {
                display: flex;
                justify-content: space-between;
                margin-bottom: 20px;
            }

            .creator-form__header {
                margin: 0;
                padding-bottom: 6px;
            }

            .creator-form__photo-upload-wrapper {
                display: flex;
                margin-bottom: 10px;
            }

            .creator-form__photo-area {
                border: 1px dashed gray;
                padding: 20px;
            }

            .creator-form__photo-btn {
                --btn-background-color: var(--main-secondary-color);
            }

            .creator-form__photo {
                height: 100%;
            }

            .creator-form__photo-subtext {
                text-align: center;
                margin-top: 6px;
                color: var(--mdc-text-field-ink-color, rgba(0, 0, 0, 0.87));
            }

            .creator-form__photo-wrapper {
                max-height: 120px;
            }

            .creator-form__link-wrapper--first {
                width: 55%;
            }

            .creator-form__link-wrapper--second {
                width: 43%;
            }

            .creator-form__input {
                display: block;
                margin: 8px 0;
                width: 100%;
            }

            .creator-form__input-group .creator-form__input {
                margin-right: 6px;
            }

            proxi-textfield, mwc-textarea {
                background-color: white;
            }

            .creator-form__social-links {
                display: flex;
            }

            .creator-form__social-icon {
                height: 18px;
                border: 1px solid var(--theme-border-color);
                border-radius: 50%;
                padding: 4px;
                background: white;
                margin-right: 10px;
                align-self: center;
            }

            .creator-form__action-button {
                --mdc-theme-primary: var(--main-success-color);
                --mdc-typography-button-text-transform: case;
                --mdc-typography-button-letter-spacing: 0px;
            }

            @media (max-width: 550px) {
                .creator-form__photo-wrapper {
                    padding-top: 20px;
                    width: 100px;
                }

                .creator-form__photo-upload-wrapper {
                    flex-wrap: wrap;
                }

            }

            @media (min-width: 551px) {
                .creator-form__input-group {
                    display: flex;
                    justify-content: space-between;
                }

            }
        `;
    }
}
